const shopConfig = JSON.parse(document.querySelector('#xcel-shop-config').textContent);
async function addCartItems(items) {
  const response = await fetch(shopConfig.add, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items }) });
  const result = await response.json();
  if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'We couldn’t add that quantity. Please try a smaller quantity or contact us.');
  return result;
}
/** Daily-total supply arithmetic; frequency does not multiply the total. All weights are whole pounds, never a medical assessment. */
const DEFAULT_TABLETS = 60;
const STORAGE_KEY = 'xcel:supply-planner:v1';
const PERIODS = [30, 60, 90];

function doseForWeight(weight) {
  if (!Number.isInteger(weight) || weight < 1 || weight > 180) {
    return { status: 'invalid', perUse: null, daily: null };
  }
  // Merchant confirmed daily totals and inclusive 75 lb boundary on 2026-10-16.
  const perUse = weight <= 25 ? 0.25 : weight <= 50 ? 0.5 : weight <= 75 ? 1 : 1.5;
  return { status: 'valid', perUse, daily: perUse };
}

function calculateSupply(dogs, days = 90, product = { tablets: DEFAULT_TABLETS, priceCents: 0 }) {
  if (!Number.isInteger(product.tablets) || product.tablets < 1 || !Number.isInteger(product.priceCents) || product.priceCents < 0) return { status: 'invalid' };
  if (!Array.isArray(dogs) || dogs.length < 1 || dogs.length > 4 || !PERIODS.includes(days)) {
    return { status: 'invalid' };
  }
  const doses = dogs.map(dog => doseForWeight(typeof dog === 'number' ? dog : dog?.weight));
  if (doses.some(dose => dose.status === 'invalid')) return { status: 'invalid' };
  if (doses.some(dose => dose.status === 'unresolved')) return { status: 'unresolved' };
  const daily = doses.reduce((sum, dose) => sum + dose.daily, 0);
  const quantity = Math.ceil(days * daily / product.tablets);
  const totalCents = quantity * product.priceCents;
  return {
    status: 'valid', days, daily, quantity, price: (product.priceCents / 100),
    total: totalCents / 100,
    coverageDays: Math.floor(quantity * product.tablets / daily),
    oneBottleDays: Math.floor(product.tablets / daily),
    costPerDay: (product.priceCents / 100) * daily / product.tablets,
    requiredTablets: days * daily,
    suppliedTablets: quantity * product.tablets,
  };
}

const tabletNumber = number => ({ 0.25: '¼', 0.5: '½', 1: '1', 1.5: '1½', 2: '2', 3: '3' }[number] ?? String(number));
const tabletText = number => `${tabletNumber(number)} ${number <= 1 ? 'tablet' : 'tablets'}`;
const cash = value => new Intl.NumberFormat(shopConfig.locale || 'en-US', { style: 'currency', currency: shopConfig.currency || 'USD' }).format(value);

function initialState() {
  const fallback = { dogs: [{ id: 'dog-1', name: 'Your dog', weight: 40 }], selected: 'dog-1', days: 90, unit: 'lb' };
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!saved || !Array.isArray(saved.dogs) || saved.dogs.length < 1 || saved.dogs.length > 4) return fallback;
    const dogs = saved.dogs.map((dog, index) => ({
      id: `dog-${index + 1}`,
      name: typeof dog.name === 'string' ? dog.name.slice(0, 24) : `Dog ${index + 1}`,
      weight: Number.isInteger(dog.weight) && dog.weight >= 1 && dog.weight <= 180 ? dog.weight : 40,
    }));
    const selectedIndex = saved.dogs.findIndex(dog => dog.id === saved.selected);
    return { dogs, selected: dogs[Math.max(0, selectedIndex)].id, days: PERIODS.includes(saved.days) ? saved.days : 90, unit: saved.unit === 'kg' ? 'kg' : 'lb' };
  } catch { return fallback; }
}

function initPlanner(root = document) {
  const planner = root.matches?.('[data-planner]') ? root : root.querySelector('[data-planner]');
  if (!planner || planner.dataset.initialized === 'true') return null;
  planner.dataset.initialized = 'true';
  const state = initialState();
  const product = { tablets: Number(planner.dataset.tablets), priceCents: Number(planner.dataset.priceCents), variantId: planner.dataset.variantId, available: planner.dataset.available === 'true' };
  let submitting = false;
  const find = selector => planner.querySelector(selector);
  const all = selector => [...planner.querySelectorAll(selector)];
  const set = (selector, value) => { const element = find(selector); if (element) element.textContent = value; };
  const dog = () => state.dogs.find(item => item.id === state.selected) || state.dogs[0];
  const save = () => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* Private browsing may disable storage. */ } };

  function renderTabs() {
    const tabs = find('[data-dog-tabs]');
    if (!tabs) return;
    tabs.replaceChildren(...state.dogs.map(item => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `dog-tab${item.id === state.selected ? ' is-active' : ''}`;
      button.dataset.selectDog = item.id;
      button.setAttribute('aria-pressed', String(item.id === state.selected));
      button.textContent = item.name.trim() || 'Your dog';
      return button;
    }));
  }

  function renderDose(dose) {
    const slot = find('[data-dose-graphic]');
    if (!slot) return;
    const doseKey = `${dose.status}:${dose.perUse}`;
    if (slot.dataset.doseKey === doseKey) return;
    slot.dataset.doseKey = doseKey;
    slot.replaceChildren();
    if (dose.status !== 'valid') {
      slot.classList.add('is-unresolved');
      slot.setAttribute('aria-label', 'Serving amount unavailable');
      const mark = document.createElement('span');
      mark.className = 'dose-unresolved';
      mark.textContent = '?';
      slot.append(mark);
      return;
    }
    slot.classList.remove('is-unresolved');
    slot.setAttribute('aria-label', `${tabletText(dose.daily)} daily total`);
    const filled = dose.perUse * 4;
    for (let index = 0; index < Math.ceil(dose.perUse); index += 1) {
      const tablet = document.createElement('span');
      tablet.className = 'dose-tablet';
      tablet.setAttribute('aria-hidden', 'true');
      for (let quarter = 0; quarter < 4; quarter += 1) {
        const part = document.createElement('i');
        part.className = `dose-quarter dose-quarter-${quarter + 1}${index * 4 + quarter < filled ? ' is-filled' : ''}`;
        tablet.append(part);
      }
      slot.append(tablet);
    }
  }

  function render({ preserveWeightInput = false, preserveNameInput = false, message = '' } = {}) {
    const selected = dog();
    const dose = doseForWeight(selected.weight);
    const plan = calculateSupply(state.dogs, state.days, product);
    planner.dataset.planStatus = plan.status;
    renderTabs();
    const nameInput = find('[data-dog-name]');
    if (nameInput && !preserveNameInput) nameInput.value = selected.name;
    const weightInput = find('[data-dog-weight]');
    if (weightInput && !preserveWeightInput) weightInput.value = selected.weight ?? '';
    if (weightInput) weightInput.setAttribute('aria-invalid', String(dose.status === 'invalid'));
    const slider = find('[data-dog-range]');
    if (slider && Number.isInteger(selected.weight)) {
      slider.value = selected.weight;
      slider.style.setProperty('--range-progress', `${(selected.weight - 1) / 179 * 100}%`);
      slider.setAttribute('aria-valuetext', `${selected.weight} pounds`);
    }
    set('[data-weight-display]', selected.weight === null ? '—' : state.unit === 'kg' ? (selected.weight * 0.45359237).toFixed(1) : selected.weight);
    set('[data-weight-unit]', state.unit);
    set('[data-weight-hint]', state.unit === 'kg' ? 'Display in kg · enter whole pounds' : 'Whole pounds · 1–180 lb');
    all('[data-unit]').forEach(button => { const active = button.dataset.unit === state.unit; button.setAttribute('aria-pressed', String(active)); button.classList.toggle('is-active', active); });
    all('[data-period]').forEach(button => { const active = Number(button.dataset.period) === state.days; button.setAttribute('aria-pressed', String(active)); button.classList.toggle('is-active', active); });
    all('[data-weight-step]').forEach(button => { button.disabled = (Number(button.dataset.weightStep) < 0 && selected.weight <= 1) || (Number(button.dataset.weightStep) > 0 && selected.weight >= 180); });
    const addDog = find('[data-add-dog]');
    if (addDog) { addDog.disabled = state.dogs.length >= 4; addDog.textContent = state.dogs.length >= 4 ? '4 dogs added' : '+ Add a dog'; }
    const removeDog = find('[data-remove-dog]');
    if (removeDog) removeDog.hidden = state.dogs.length < 2;
    renderDose(dose);
    set('[data-dose-per-use]', dose.status === 'valid' ? tabletText(dose.perUse) : 'Check label');
    set('[data-daily-tablets]', dose.status === 'valid' ? `${tabletText(dose.daily)} per day` : 'Serving amount needs confirmation');
    const valid = plan.status === 'valid';
    set('[data-duration-number]', valid ? plan.oneBottleDays : '—');
    set('[data-duration-caption]', `${state.dogs.length > 1 ? 'For all your dogs' : 'For ' + (selected.name.trim() || 'your dog')} · 60 tablets per bottle`);
    const durationBar = find('[data-duration-bar]');
    if (durationBar) durationBar.style.width = valid ? `${Math.min(100, plan.oneBottleDays / 240 * 100)}%` : '0%';
    set('[data-bottle-count]', valid ? plan.quantity : '—');
    set('[data-bottle-label]', valid && plan.quantity === 1 ? 'bottle' : 'bottles');
    set('[data-single-bottle-days]', valid ? `${plan.oneBottleDays} days${state.dogs.length > 1 ? ' · all dogs' : ''}` : '—');
    set('[data-coverage-days]', valid ? `${plan.coverageDays} days` : '—');
    set('[data-plan-total]', valid ? cash(plan.total) : '—');
    set('[data-unit-price]', cash(product.priceCents / 100));
    set('[data-day-cost]', valid ? cash(plan.costPerDay) : '—');
    set('[data-household-daily]', valid ? `${tabletText(plan.daily)} per day` : '—');
    set('[data-result-summary]', `For ${state.dogs.length > 1 ? `${state.dogs.length} dogs over` : 'your'} ${state.days}${state.dogs.length > 1 ? ' days' : '-day routine'}`);
    let statusMessage = message;
    if (plan.status === 'unresolved') statusMessage = 'The daily amount for exactly 75 lb is awaiting confirmation. Please contact us for a supply estimate.';
    if (plan.status === 'invalid') statusMessage = 'Enter a whole number from 1 to 180 lb for each dog to see your supply plan.';
    if (valid && !product.available) statusMessage = 'This bottle option is currently sold out.';
    set('[data-plan-message]', statusMessage);
    const add = find('[data-plan-add]');
    if (add) {
      add.disabled = !valid || !product.available || submitting || !product.variantId;
      add.textContent = submitting ? 'Adding…' : !product.available ? 'Sold out' : valid ? `Add ${plan.quantity} ${plan.quantity === 1 ? 'bottle' : 'bottles'} to bag ↗` : 'Supply plan unavailable';
    }
    save();
  }

  planner.addEventListener('input', event => {
    if (event.target.matches('[data-dog-name]')) {
      dog().name = event.target.value.slice(0, 24);
      renderTabs(); save();
    } else if (event.target.matches('[data-dog-weight], [data-dog-range]')) {
      const value = event.target.value;
      dog().weight = value.trim() === '' ? null : Number(value);
      render({ preserveWeightInput: event.target.matches('[data-dog-weight]') });
    }
  });

  planner.addEventListener('click', async event => {
    const button = event.target.closest('button');
    if (!button || !planner.contains(button) || button.disabled) return;
    if (button.hasAttribute('data-select-dog')) {
      state.selected = button.dataset.selectDog;
      render();
      find(`[data-select-dog="${state.selected}"]`)?.focus({ preventScroll: true });
    } else if (button.hasAttribute('data-add-dog') && state.dogs.length < 4) {
      const id = `dog-${Date.now()}-${state.dogs.length}`;
      state.dogs.push({ id, name: `Dog ${state.dogs.length + 1}`, weight: 40 });
      state.selected = id; render();
      find('[data-dog-name]')?.focus({ preventScroll: true });
      find('[data-dog-name]')?.select();
    } else if (button.hasAttribute('data-remove-dog') && state.dogs.length > 1) {
      state.dogs = state.dogs.filter(item => item.id !== state.selected);
      state.selected = state.dogs[0].id; render();
      find(`[data-select-dog="${state.selected}"]`)?.focus({ preventScroll: true });
    } else if (button.hasAttribute('data-weight-step')) {
      dog().weight = Math.max(1, Math.min(180, (Number.isInteger(dog().weight) ? dog().weight : 40) + Number(button.dataset.weightStep)));
      render();
    } else if (button.hasAttribute('data-unit')) {
      state.unit = button.dataset.unit === 'kg' ? 'kg' : 'lb'; render();
    } else if (button.hasAttribute('data-period')) {
      state.days = Number(button.dataset.period); render();
    } else if (button.hasAttribute('data-plan-add')) {
      const plan = calculateSupply(state.dogs, state.days, product);
      if (plan.status !== 'valid') return;
      if (!product.available || submitting || !product.variantId) return;
      submitting = true; render();
      try {
        await addCartItems([{ id: product.variantId, quantity: plan.quantity }]);
        location.assign(shopConfig.cart);
      } catch (error) {
        submitting = false; render({ message: error.message });
      }
    }
  });

  planner.querySelector('[data-planner-variant]')?.addEventListener('change', event => {
    const option = event.target.selectedOptions[0];
    product.variantId = option.value;
    product.priceCents = Number(option.dataset.priceCents);
    product.available = option.dataset.available === 'true';
    render();
  });
  render();
  return { getState: () => JSON.parse(JSON.stringify(state)), getPlan: () => calculateSupply(state.dogs, state.days, product) };
}

function initTablet(root = document) {
  const tablet = root.querySelector('#tabletModel');
  const stage = root.querySelector('.photo-stage');
  const controls = root.querySelectorAll('[data-tablet-mode]');
  const hint = root.querySelector('#tabletHint');
  if (!tablet || !stage || !controls.length) return;
  controls.forEach(button => button.addEventListener('click', () => {
    const split = button.dataset.tabletMode === 'split';
    tablet.classList.toggle('split', split);
    stage.classList.toggle('is-split', split);
    controls.forEach(control => control.setAttribute('aria-pressed', String(control === button)));
    tablet.setAttribute('aria-label', split ? 'Product tablet visual separated into four quarter portions' : 'Product tablet visual showing four scored portions');
    hint.textContent = split ? 'Four quarter portions. One thoughtfully scored tablet.' : 'Explore how one tablet separates into four portions.';
  }));
}

function initializeTheme(root = document) {
  root.querySelectorAll('[data-planner]').forEach(planner => initPlanner(planner));
  if (!root.querySelector('#tabletModel')?.dataset.initialized) {
    initTablet(root);
    const tablet = root.querySelector('#tabletModel');
    if (tablet) tablet.dataset.initialized = 'true';
  }
  const film = root.querySelector('#heroFilm');
  const control = root.querySelector('#videoToggle');
  if (film && control && !control.dataset.initialized) {
    control.dataset.initialized = 'true';
    const sync = () => { control.setAttribute('aria-label', film.paused ? 'Play background video' : 'Pause background video'); control.querySelector('span').textContent = film.paused ? 'PLAY FILM' : 'PAUSE FILM'; control.querySelector('path').setAttribute('d', film.paused ? 'M7 5l8 5-8 5Z' : 'M7 5v10M13 5v10'); };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) film.pause();
    control.addEventListener('click', () => { if (film.paused) film.play().catch(() => {}); else film.pause(); });
    film.addEventListener('play', sync); film.addEventListener('pause', sync); sync();
  }
}
function syncHeader() {
  const header = document.querySelector('#siteHeader');
  const menu = document.querySelector('#mobileNav');
  header?.classList.toggle('scrolled', scrollY > 55 || !document.body.classList.contains('has-hero') || (menu && !menu.hidden));
}
function setMenu(open) {
  const menu = document.querySelector('#mobileNav'), button = document.querySelector('#menuToggle');
  if (!menu || !button) return;
  menu.hidden = !open; button.setAttribute('aria-expanded', String(open)); button.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation'); syncHeader();
}
addEventListener('scroll', syncHeader, { passive: true });
document.addEventListener('keydown', event => { if (event.key === 'Escape') setMenu(false); });
document.addEventListener('click', event => {
  if (event.target.closest('#menuToggle')) setMenu(document.querySelector('#mobileNav').hidden);
  if (event.target.closest('#mobileNav a')) setMenu(false);
  const button = event.target.closest('[data-gallery-src]');
  if (button) {
    const gallery = button.closest('[data-product-gallery]');
    const image = gallery.querySelector('[data-gallery-image]');
    if (image) { image.src = button.dataset.gallerySrc; image.removeAttribute('srcset'); image.alt = button.dataset.galleryAlt; }
    gallery.querySelectorAll('[data-gallery-src]').forEach(other => { other.classList.toggle('active', other === button); other.setAttribute('aria-pressed', String(other === button)); });
  }
});
document.addEventListener('change', event => {
  if (!event.target.matches('[data-variant-select]')) return;
  const option = event.target.selectedOptions[0];
  const form = event.target.closest('form');
  const button = form.querySelector('[data-add-product]');
  const available = option.dataset.available === 'true';
  button.disabled = !available; button.textContent = available ? 'Add to bag +' : 'Sold out';
  const price = form.closest('.product-copy')?.querySelector('[data-product-price]');
  if (price) price.textContent = option.dataset.price;
  if (document.body.classList.contains('template-product')) { const url = new URL(location.href); url.searchParams.set('variant', option.value); history.replaceState({}, '', url); }
});
document.addEventListener('submit', async event => {
  const form = event.target.closest('[data-product-form]');
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('[data-add-product]');
  const error = form.querySelector('[data-form-error]');
  if (button.disabled) return;
  const values = new FormData(form), quantity = Number(values.get('quantity'));
  if (!Number.isInteger(quantity) || quantity < 1) { error.textContent = 'Enter a whole quantity of at least 1.'; return; }
  const previous = button.textContent; button.disabled = true; button.textContent = 'Adding…'; error.textContent = '';
  try { await addCartItems([{ id: values.get('id'), quantity }]); location.assign(shopConfig.cart); }
  catch (failure) { error.textContent = failure.message; button.disabled = false; button.textContent = previous; }
});
document.addEventListener('shopify:section:load', event => { initializeTheme(event.target); syncHeader(); });
initializeTheme(); syncHeader();
